import React, { useState, useEffect, useRef } from 'react';
import ApplicationTable from './ApplicationTable';
import ApplicationKanban from './ApplicationKanban';
import { useParams } from 'react-router-dom';
// Hook to fetch vacancies: This hook calls vacancyService.getVacancies()
// which makes API call: GET /vacancies/listing?company_id={companyId}
// Returns data in format: { vacancies: [...] } (or similar structure based on API response)
import { useVacancies } from '../../hooks/queries/usevacancy';
import { useApplicationsByVacancyId } from '../../hooks/queries/useApplication';
import { useLocation } from 'react-router-dom';
import Dropdown from '@/components/ui/Dropdown';
import { Building2, Flag, Minus } from 'lucide-react';
import Stats from '@/components/Companies/Applications/Stats';

const statusOptions = [
  { value: 'in review', label: 'In Review' },
  { value: 'shortlisted', label: 'Shortlisted' },
  { value: 'interviewScheduled', label: 'Interview Scheduled' },
  { value: 'interviewed', label: 'Interviewed' },
  { value: 'offered', label: 'Offered' },
  { value: 'hired', label: 'Hired' },
  { value: 'rejected', label: 'Rejected' }
];

const Applications = () => {
  const [viewMode, setViewMode] = useState('stats'); // Changed from 'kanban' to 'stats'
  const [selectedVacancy, setSelectedVacancy] = useState(null);
  const [showVacanciesDropdown, setShowVacanciesDropdown] = useState(false);
  const [initialFilters, setInitialFilters] = useState(null);
  const [selectedStatuses, setSelectedStatuses] = useState(new Set());
  const { id } = useParams(); // Get company ID from URL params
  
  // FETCH VACANCIES FOR DROPDOWN:
  // This hook fetches all vacancies for the current company using getVacancies API
  // Data flow: useVacancies -> vacancyService.getVacancies() -> API: GET /vacancy/listing?company_id={id}
  // Response structure: { vacancies: [{ id, title, status, ... }, ...] } (or similar based on API)
  // Params: Filter by status [0, 1] to get active vacancies only
  const { data: vacancies } = useVacancies(id, { status: [0, 1] });
  const location = useLocation();
  const dropdownRef = useRef(null);

  // GET /vacancy/{id}/applications — use vacancy.foundation_members for toolbar flag
  const { data: applicationsMeta } = useApplicationsByVacancyId(
    selectedVacancy?.id,
    {
      per_page: 1,
      page: 1,
      queryKey: ["applications-foundation-flag", selectedVacancy?.id],
    },
  );
  const isFoundationMembers =
    applicationsMeta?.vacancy?.foundation_members === 1 ||
    applicationsMeta?.vacancy?.foundation_members === true;

  const handleStatusClick = (status) => {
    console.log("Status clicked:", status);
    console.log("Status ID:", status?.vacancy_status_id);
    console.log("Status name:", status?.status_name);
    
    // The status_summary uses vacancy_status_id, but we need to find the corresponding status.id
    // from the selectedVacancy.vacancy_statuses
    const matchingStatus = selectedVacancy?.vacancy_statuses?.find(
      vs => vs.status.id === status?.vacancy_status_id || vs.id === status?.vacancy_status_id
    );
    
    const statusId = matchingStatus?.status?.id || status?.vacancy_status_id;
    const statusName = matchingStatus?.status?.name || status?.status_name;
    
 
    // Use status ID for filtering to match dropdown options
    setInitialFilters({stage: [statusId]});
    console.log("Setting initial filters:", {stage: [statusId]}, "Status name:", statusName);
    setViewMode('table');
  };

  // Add this useEffect to handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowVacanciesDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Modify the useEffect to set view mode to 'table' when coming from Applicants
  useEffect(() => {
    const savedViewMode = localStorage.getItem(`viewMode_${id}`);
    if (location.state?.fromApplicants) {
      setViewMode('table');
      location.state.fromApplicants = false;
    } else if (savedViewMode) {
      setViewMode(savedViewMode);
    }

    // Get selectedVacancy from location state if available
    const locationStateVacancy = location.state?.selectedVacancy;
    if (locationStateVacancy && vacancies?.vacancies) {
      const foundVacancy = vacancies.vacancies.find(v => v.id === locationStateVacancy);
      if (foundVacancy) {
        setSelectedVacancy(foundVacancy);
        return;
      }
    }

    const savedSelectedVacancy = localStorage.getItem(`selectedVacancy_${id}`);
    if (savedSelectedVacancy && vacancies?.vacancies) {
      const parsedVacancy = JSON.parse(savedSelectedVacancy);
      const foundVacancy = vacancies.vacancies.find(v => v.id === parsedVacancy.id);
      if (foundVacancy) {
        setSelectedVacancy(foundVacancy);
      }
    }
  }, [id, vacancies, location.state?.selectedVacancy, location.state?.fromApplicants]);

  // Save viewMode to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(`viewMode_${id}`, viewMode);
  }, [viewMode, id]);

  // Save selectedVacancy to localStorage when it changes
  useEffect(() => {
    if (selectedVacancy) {
      localStorage.setItem(`selectedVacancy_${id}`, JSON.stringify(selectedVacancy));
    }
  }, [selectedVacancy, id]);

  // Initialize all statuses as checked when vacancy is selected
  useEffect(() => {
    if (selectedVacancy?.vacancy_statuses) {
      // Try to load from localStorage first
      const savedStatuses = localStorage.getItem(`selectedStatuses_${id}_${selectedVacancy.id}`);
      if (savedStatuses) {
        try {
          const parsedStatuses = JSON.parse(savedStatuses);
          setSelectedStatuses(new Set(parsedStatuses));
        } catch (error) {
          console.error('Error parsing saved statuses:', error);
          // Fallback to all statuses checked
          const allStatusIds = new Set(selectedVacancy.vacancy_statuses.map(status => status.id));
          setSelectedStatuses(allStatusIds);
        }
      } else {
        // Default: all statuses checked
        const allStatusIds = new Set(selectedVacancy.vacancy_statuses.map(status => status.id));
        setSelectedStatuses(allStatusIds);
      }
    }
  }, [selectedVacancy, id]);

  // Save selected statuses to localStorage when they change
  useEffect(() => {
    if (selectedVacancy && selectedStatuses.size > 0) {
      const statusArray = Array.from(selectedStatuses);
      localStorage.setItem(`selectedStatuses_${id}_${selectedVacancy.id}`, JSON.stringify(statusArray));
    }
  }, [selectedStatuses, selectedVacancy, id]);

  const handleVacancySelect = (vacancy) => {
    setSelectedVacancy(vacancy);
    setShowVacanciesDropdown(false);
  };

  const handleStatusToggle = (statusId) => {
    setSelectedStatuses(prev => {
      const newSet = new Set(prev);
      if (newSet.has(statusId)) {
        newSet.delete(statusId);
      } else {
        newSet.add(statusId);
      }
      return newSet;
    });
  };

  return (
    <div className="w-full">
      <div className="flex justify-start items-center mb-4 gap-4">
        {/* Vacancy Selector */}
        <div>
          {/* 
            VACANCIES DROPDOWN OPTIONS:
            - Source: vacancies?.vacancies (nested structure from API response)
            - Filter: Exclude archived vacancies (status !== 2)
            - Transform: Map each vacancy to { label: title, value: vacancy object }
            - Fallback: Empty array [] if vacancies data is not loaded yet
          */}
          <Dropdown
          options={vacancies?.vacancies?.filter(vacancy => vacancy.status !== 2).map(vacancy => ({
              label: vacancy.title,
              value: vacancy
            })) || []}
            value={selectedVacancy ? { label: selectedVacancy.title, value: selectedVacancy } : null}
            onChange={(selected) => handleVacancySelect(selected.value)}
            placeholder="Select Vacancy"
            icon={<Building2 className="w-5 h-5 text-foreground flex-shrink-0" />}
            searchable={true}
            className="w-auto min-w-[200px]"
            renderOption={(option) => (
              <p className="text-foreground">
                {option.label}
              </p>
            )}
          />
        </div>

        {/* View Mode Toggle */}
        <div className="inline-flex rounded-lg border border-border bg-card p-1 h-[53px]">
        <button
            onClick={() => {
              setViewMode('stats');
              setInitialFilters(null);
            }}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              viewMode === "stats"
                ? "bg-primary-dark text-white transition-colors"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Stats
          </button>
          
          <button
            onClick={() => setViewMode("table")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              viewMode === "table"
                ? "bg-primary-dark text-white transition-colors"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Table
          </button>
          <button
            onClick={() => setViewMode("kanban")}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              viewMode === "kanban"
                ? "bg-primary-dark text-white transition-colors"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            Kanban
          </button>
        </div>

        {isFoundationMembers && (
          <div
            className="inline-flex items-center gap-2 h-[53px] p-3 rounded-lg border border-primary-dark/20 bg-gradient-to-r from-primary-dark to-primary-dark/90 text-white shadow-sm"
            title="This vacancy is for Foundation Members"
          >
            <span className="flex items-center justify-center w-6 h-6 rounded-md bg-white/15">
              <Flag className="w-3.5 h-3.5 fill-white/90" strokeWidth={2} />
            </span>
            <div className="flex flex-col leading-tight">
              
              <span className="text-sm font-semibold whitespace-nowrap">
                Foundation Members
              </span>
            </div>
          </div>
        )}

        {
          viewMode === 'kanban' && 
          <div className='inline-flex items-center gap-4'>
            <Minus className='rotate-90 w-12 h-12' size={256} strokeWidth={1} />
            <Dropdown
            options={selectedVacancy?.vacancy_statuses?.map(status => ({
                label: status.status.name,
                value: status
              })) || []}
              value={null}
              onChange={(selected) => {
                // Handle status selection if needed
                console.log('Selected status:', selected.value);
              }}
              placeholder="Select Status"
              icon={<Building2 className="w-5 h-5 text-foreground flex-shrink-0" />}
              searchable={true}
              className="w-auto min-w-[200px]"
              renderOption={(option) => (
                <div 
                  className="flex items-start gap-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={selectedStatuses.has(option.value.id)}
                    onChange={() => handleStatusToggle(option.value.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 bg-muted border-input rounded  focus:ring-2 mt-1"
                  />
                  <p className="text-foreground">
                    {option.label}
                  </p>
                </div>
              )}
            />
          </div>
        }
      </div>

      {viewMode === 'kanban' ? 
        <ApplicationKanban selectedVacancy={selectedVacancy} selectedStatuses={selectedStatuses} /> : 
        viewMode === 'table' ?
        <ApplicationTable selectedVacancy={selectedVacancy} initialFilters={initialFilters} /> :
        <Stats selectedVacancy={selectedVacancy} onStatusClick={handleStatusClick} />
      }
      
    </div>
  );
};

export default Applications; 